import { useEffect, useState } from "react";
import { api, coverUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LIBRARY } from "@/constants/testIds";
import { Link, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";

export default function MyLibrary() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) nav("/login?next=/library");
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/library").then(({ data }) => setItems(data)).finally(() => setPageLoading(false));
  }, [user]);

  const download = async (product) => {
    try {
      const res = await api.get(`/files/download/${product.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = product.file_original_name || "download";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">// My Library</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 mb-8">Your collection.</h1>

      {pageLoading ? (
        <div className="font-mono text-xs text-white/50">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid={LIBRARY.emptyState} className="border border-dashed border-white/15 py-24 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">// EMPTY</div>
          <p className="mt-4 text-white/70">Nothing here yet — go grab a drop.</p>
          <Link to="/" className="inline-block mt-6 bg-[#FF3366] text-white font-mono text-xs uppercase tracking-[0.2em] px-4 py-2">
            Browse the store →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(({ purchase_id, product }) => (
            <div key={purchase_id} data-testid={LIBRARY.itemCard(product.id)} className="border border-white/10 bg-[#0A0A0A]">
              <div className="aspect-[16/10] bg-[#141414] overflow-hidden">
                <img src={coverUrl(product.cover_storage_path)} className="w-full h-full object-cover" alt="" />
              </div>
              <div className="p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{product.category}</div>
                <div className="font-display font-bold text-lg mt-1 truncate">{product.title}</div>
                <div className="mt-4 flex items-center justify-between">
                  <Link to={`/p/${product.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white">Details →</Link>
                  <button
                    data-testid={LIBRARY.downloadBtn(product.id)}
                    onClick={() => download(product)}
                    className="inline-flex items-center gap-2 bg-[#CCFF00] text-black font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 hover:opacity-90"
                  >
                    <Download className="w-3 h-3" /> Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
